import { useEffect, useRef } from "react";
import { Button } from "../atoms/Button/Button.js";
import { EmptyState } from "../molecules/EmptyState/EmptyState.js";

/** One entry in the mention picker: Player AI, or a person in the chat. */
export interface ComposerMentionOption {
  readonly id: string;
  readonly label: string;
  readonly kind: "player_ai" | "person";
}

/** The DOM id of one mention option, so the textarea can point at it. */
export function mentionOptionDomId(
  listId: string,
  option: ComposerMentionOption,
): string {
  return option.kind === "player_ai"
    ? `${listId}-player-ai`
    : `${listId}-person-${option.id}`;
}

/**
 * The composer's two hand-built combobox listboxes — the mention picker
 * (Player AI and, in a group conversation, the people in it) and the
 * `/next_phase` command suggestion (PB-5422 chat-parts-private). Split into a
 * sibling of PlayerAIComposer.tsx to keep that file under the 200-line soft
 * cap (packages/ui/CLAUDE.md invariant 6).
 */
export function ComposerSuggestionMenus({
  mentionMenuOpen,
  mentionOptions,
  mentionStatus,
  onRetryMentions,
  activeMentionIndex,
  mentionListId,
  onSelectMention,
  slashMenuOpen,
  nextPhaseCommandAvailable,
  slashListId,
  slashOptionId,
  onSelectNextPhaseCommand,
}: {
  mentionMenuOpen: boolean;
  mentionOptions: readonly ComposerMentionOption[];
  mentionStatus?: "loading" | "error" | "ready" | undefined;
  onRetryMentions?: (() => void) | undefined;
  activeMentionIndex: number;
  mentionListId: string;
  onSelectMention: (option: ComposerMentionOption) => void;
  slashMenuOpen: boolean;
  nextPhaseCommandAvailable: boolean | undefined;
  slashListId: string;
  slashOptionId: string;
  onSelectNextPhaseCommand: () => void;
}) {
  const activeOption = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (mentionMenuOpen)
      activeOption.current?.scrollIntoView?.({ block: "nearest" });
  }, [mentionMenuOpen, activeMentionIndex, mentionOptions]);
  return (
    <>
      {mentionMenuOpen ? (
        <div className="absolute bottom-full start-2 z-popover mb-1 w-64 rounded-md border border-pb-border-strong bg-pb-card p-1 shadow-xl">
          {mentionStatus === "loading" ? (
            <p
              role="status"
              aria-label="Loading members"
              className="px-2 py-1 text-xs text-pb-text-muted"
            >
              Loading members…
            </p>
          ) : null}
          {mentionStatus === "error" ? (
            <EmptyState
              variant="error"
              size="sm"
              role="alert"
              title="Couldn’t load member names."
              action={
                <Button size="sm" variant="ghost" onClick={onRetryMentions}>
                  Retry
                </Button>
              }
            />
          ) : null}
          {mentionStatus === "ready" &&
          !mentionOptions.some((option) => option.kind === "person") ? (
            <p role="status" className="px-2 py-1 text-xs text-pb-text-muted">
              No matching members.
            </p>
          ) : null}
          <div
            id={mentionListId}
            role="listbox"
            aria-label="Mention suggestions"
          >
            {/* A long people list scrolls inside the frame, and the active
              option takes the fill from here, so the listbox and option
              strings stay the ones TriggerAutocomplete mirrors. High
              contrast rings it, because the tint alone is too faint (d15). */}
            <div className="max-h-44 overflow-y-auto [&>[aria-selected=true]]:bg-pb-iris-soft [&>[aria-selected=true]]:hc:ring-2 [&>[aria-selected=true]]:hc:ring-inset [&>[aria-selected=true]]:hc:ring-pb-iris">
              {mentionOptions.map((option, index) => (
                <button
                  key={`${option.kind}:${option.id}`}
                  ref={index === activeMentionIndex ? activeOption : undefined}
                  id={mentionOptionDomId(mentionListId, option)}
                  type="button"
                  role="option"
                  aria-selected={index === activeMentionIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onSelectMention(option)}
                  className="flex w-full items-center rounded px-2 py-1.5 text-start text-sm text-pb-text outline-none hover:bg-pb-iris-soft focus-visible:bg-pb-iris-soft"
                >
                  <span className="min-w-0 truncate">@{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {slashMenuOpen && nextPhaseCommandAvailable ? (
        <div
          id={slashListId}
          role="listbox"
          aria-label="Command suggestions"
          className="absolute bottom-full start-2 z-popover mb-1 w-48 rounded-md border border-pb-border-strong bg-pb-card p-1 shadow-xl"
        >
          <button
            id={slashOptionId}
            type="button"
            role="option"
            aria-selected="true"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onSelectNextPhaseCommand}
            className="flex w-full items-center rounded px-2 py-1.5 text-start text-sm text-pb-text outline-none hover:bg-pb-cyan-soft focus-visible:bg-pb-cyan-soft"
          >
            /next_phase
          </button>
        </div>
      ) : null}
    </>
  );
}
